import * as CSS from "csstype";
import {
  Box,
  Button,
  Divider,
  HStack,
  Spacer,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import { ForwardIcon, TranslateIcon } from "modules/icons";
import { useAuth } from "modules/providers/auth-provider";
import React, { memo, RefObject, useMemo, useState } from "react";
import { useOverflowDetector } from "react-detectable-overflow";
import colors from "app/colors";
import {
  MESSAGE_NOT_SENT_OPACITY,
  MESSAGE_NOT_SENT_SPINNER_SPEED,
  MESSAGE_STATE,
  MessageSubType,
} from "modules/utils/Constants";
import useMessageBackground from "modules/hooks/use-message-background";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import LinkedConversationsPopover from "./linked-conversations-popover";
import MessageFiles from "./message-files";
import MessageGifViewer from "./message-gif-viewer";
import MessageReactions from "./message-reactions";
import MessageSeen from "./message-seen";
import MessageText from "./message-text";
import EmailMessage from "./email-message";
import QuotedMessages from "./quoted-messages";
import UserMessageHeader from "./user-message-header";
import { MESSAGE_SEEN_HOVER_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  messageState: MESSAGE_STATE;
}

const MAX_COLLAPSED_HEIGHT = 400;

// message ids the user expanded via "Read More"; module-scoped so the
// expansion survives the virtualized item unmounting and remounting as the
// user scrolls away and back
const expandedMessageIds = new Set<string>();

const UserMessage: React.FC<IProps> = ({
  messageThread,
  message,
  messageState,
}) => {
  const { me } = useAuth();

  const { messageBackground } = useMessageBackground({ message });

  const { ref: overflowRef, overflow } = useOverflowDetector({});

  const [isExpanded, setIsExpanded] = useState(() =>
    expandedMessageIds.has(message.id),
  );

  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const gifList = message.gifList;

  const reactions = useMemo(
    () => message.reactionList.filter((reaction) => !reaction.fileId),
    [message],
  );

  const relatedMessageThreads = message.relatedMessageThreadList;

  const messageFontStyle = useMemo((): CSS.Property.FontStyle => {
    if (message.subType === MessageSubType.OUT_OF_OFFICE) {
      return "italic";
    }
    return "normal";
  }, [message.subType]);

  return (
    <UserMessageHeader
      messageThread={messageThread}
      message={message}
      messageState={messageState}
    >
      <Box
        alignItems="start"
        bg={messageBackground.backgroundColor}
        roundedTopRight="2xl"
        roundedBottomRight="2xl"
        roundedBottomLeft="2xl"
        transition={messageBackground.transition}
        w="100%"
        pt="10px"
        pr="12px"
        pb="10px"
        pl={isViewingTranslatedMessage ? "4px" : "12px"}
      >
        {message.deleted ? (
          <Text fontStyle="italic" color={colors.grayMain} fontSize="sm">
            This message has been deleted
          </Text>
        ) : (
          <>
            <QuotedMessages messageThread={messageThread} message={message} />
            {message.type === MessageType.User && message.text && (
              <VStack spacing={1} w="full" alignItems="flex-start">
                {message.subType === MessageSubType.OUT_OF_OFFICE && (
                  <Text
                    fontSize={12}
                    fontWeight={400}
                    color={colors.grayMain}
                    fontStyle="italic"
                  >
                    (Automatic response)
                  </Text>
                )}
                <HStack
                  alignItems="flex-start"
                  ref={overflowRef as RefObject<HTMLDivElement>}
                  maxH={isExpanded ? undefined : MAX_COLLAPSED_HEIGHT}
                  overflow={isExpanded ? undefined : "clip"}
                >
                  {isViewingTranslatedMessage && (
                    <TranslateIcon boxSize={3} fill={colors.grayMain} mt={1} />
                  )}
                  <Box
                    color={colors.blackSecondary}
                    fontWeight={400}
                    fontSize={14}
                    fontStyle={messageFontStyle}
                    sx={{ margin: "0 !important" }}
                  >
                    <MessageText
                      messageThread={messageThread}
                      message={message}
                    />
                  </Box>
                </HStack>
              </VStack>
            )}

            <EmailMessage
              messageThread={messageThread}
              message={message}
              messageState={messageState}
            />

            {overflow && !isExpanded && (
              <Box>
                <Button
                  size="xs"
                  variant="ghost"
                  color={colors.blue}
                  onClick={() => {
                    expandedMessageIds.add(message.id);
                    setIsExpanded(true);
                  }}
                >
                  + Read More
                </Button>
              </Box>
            )}
            {gifList.length > 0 && (
              <Box
                pb="8px"
                opacity={
                  messageState === "not-sent" ? MESSAGE_NOT_SENT_OPACITY : 1
                }
              >
                {gifList.map((gif, index) => (
                  <Box
                    key={index}
                    pl={isViewingTranslatedMessage ? 3 : 0}
                    pb={1}
                  >
                    <MessageGifViewer
                      messageThread={messageThread}
                      message={message}
                      gif={gif}
                    />
                  </Box>
                ))}
              </Box>
            )}
            {message.fileList.length > 0 && (
              <>
                {message.type === MessageType.Email && (
                  <HStack w="full">
                    <Text
                      w={165}
                      color={colors.gray50}
                      fontSize={12}
                      fontWeight={700}
                    >
                      Email Attachments
                    </Text>
                    <Divider borderColor={colors.gray30} borderStyle="dashed" />
                  </HStack>
                )}
                <MessageFiles
                  messageThread={messageThread}
                  message={message}
                  messageState={messageState}
                />
              </>
            )}
            {messageState === "not-sent" ? (
              <HStack w="full" h={4}>
                <Spacer />
                <Spinner
                  color={colors.greenPrimary}
                  size="xs"
                  speed={MESSAGE_NOT_SENT_SPINNER_SPEED}
                />
              </HStack>
            ) : (
              <HStack>
                <HStack divider={<Box h={4} borderColor={colors.gray30} />}>
                  {!!reactions.length && (
                    <Box py={1} pl={isViewingTranslatedMessage ? 3 : 0}>
                      <MessageReactions message={message} />
                    </Box>
                  )}
                  {!!relatedMessageThreads.length && (
                    <LinkedConversationsPopover
                      relatedMessageThreads={relatedMessageThreads}
                    >
                      <HStack py={1} spacing={0.5} color={colors.blue}>
                        <ForwardIcon fill={colors.blue} />
                        <Text fontSize="11px" color={colors.blue}>
                          {relatedMessageThreads.length}
                        </Text>
                      </HStack>
                    </LinkedConversationsPopover>
                  )}
                </HStack>
                <Spacer />
                {message.createdUserId === me?.id && (
                  <Box
                    _hover={{ zIndex: MESSAGE_SEEN_HOVER_Z_INDEX }} // ensures the seen popover is always on top of the MessagePopover
                  >
                    <MessageSeen
                      messageThread={messageThread}
                      message={message}
                    />
                  </Box>
                )}
              </HStack>
            )}
          </>
        )}
      </Box>
    </UserMessageHeader>
  );
};

export default memo(UserMessage);
